import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useStackingZIndex } from '@/contexts/StackingContext';
import { cx } from '@/utils/cx';

/** Pixel gap between trigger and popover surface */
const TOOLTIP_GAP = 6;
/** Minimum pixel distance from viewport edge */
const VIEWPORT_PAD = 8;

export type EntityHoverPopoverPlacement = 'top' | 'bottom' | 'left' | 'right';

interface EntityHoverPopoverBaseProps {
  /** Whether the popover is open. Parent owns the open/close state. */
  open: boolean;
  /** Ref to the trigger element used as positioning anchor. */
  triggerRef: React.RefObject<HTMLElement | null>;
  /** Placement relative to trigger. Defaults to 'top'. */
  placement?: EntityHoverPopoverPlacement;
  /** Pointer enter handler — parent wires this to cancel its hide timer. */
  onPointerEnter?: () => void;
  /** Pointer leave handler — parent wires this to start its hide timer. */
  onPointerLeave?: () => void;
  /** Blur handler — parent wires this to potentially close the popover. */
  onBlur?: (e: React.FocusEvent) => void;
  /** Optional id for aria-describedby wiring. */
  id?: string;
  /** Body content to render inside the popover. */
  children: React.ReactNode;
  /** Optional className applied to the portaled popover element. */
  className?: string;
  /** Key-down handler forwarded to the popover div — used to handle Escape from within the portal. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

// WAI-ARIA requires role="dialog" to have an accessible name. Enforce at the
// type level: callers must supply either `aria-label` or `aria-labelledby`.
type EntityHoverPopoverAccessibleName =
  | { 'aria-label': string; 'aria-labelledby'?: never }
  | { 'aria-label'?: never; 'aria-labelledby': string };

export type EntityHoverPopoverProps = EntityHoverPopoverBaseProps &
  EntityHoverPopoverAccessibleName;

export function EntityHoverPopover({
  open,
  triggerRef,
  placement = 'top',
  onPointerEnter,
  onPointerLeave,
  onBlur,
  id,
  children,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  onKeyDown,
}: EntityHoverPopoverProps): React.ReactElement | null {
  const stackingZIndex = useStackingZIndex(50);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const updatePosition = useCallback(() => {
    const el = popoverRef.current;
    const trigger = triggerRef.current;
    if (!el || !trigger) {
      return;
    }

    const gap = TOOLTIP_GAP;
    const pad = VIEWPORT_PAD;
    const triggerRect = trigger.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    if (placement === 'top' || placement === 'bottom') {
      if (placement === 'top') {
        const rawTop = triggerRect.top - elRect.height - gap;
        el.style.top = `${Math.max(pad, rawTop)}px`;
        el.style.bottom = 'auto';
      } else {
        const rawTop = triggerRect.bottom + gap;
        const maxTopForBottom = window.innerHeight - elRect.height - pad;
        el.style.top = `${Math.max(pad, Math.min(rawTop, maxTopForBottom))}px`;
        el.style.bottom = 'auto';
      }

      const targetLeft =
        triggerRect.left + triggerRect.width / 2 - elRect.width / 2;
      const maxLeft = window.innerWidth - elRect.width - pad;
      const clampedLeft = Math.max(pad, Math.min(targetLeft, maxLeft));
      el.style.left = `${clampedLeft}px`;
      el.style.right = 'auto';
      el.style.transform = 'none';
    } else {
      if (placement === 'left') {
        const rawLeft = triggerRect.left - elRect.width - gap;
        el.style.left = `${Math.max(pad, rawLeft)}px`;
        el.style.right = 'auto';
      } else {
        const rawLeft = triggerRect.right + gap;
        const maxLeftForRight = window.innerWidth - elRect.width - pad;
        el.style.left = `${Math.max(pad, Math.min(rawLeft, maxLeftForRight))}px`;
        el.style.right = 'auto';
      }

      const targetTop =
        triggerRect.top + triggerRect.height / 2 - elRect.height / 2;
      const maxTop = window.innerHeight - elRect.height - pad;
      const clampedTop = Math.max(pad, Math.min(targetTop, maxTop));
      el.style.top = `${clampedTop}px`;
      el.style.bottom = 'auto';
      el.style.transform = 'none';
    }
  }, [placement, triggerRef]);

  useLayoutEffect(() => {
    if (open) {
      updatePosition();
    }
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleReposition = () => {
      updatePosition();
    };
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open, updatePosition]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      ref={popoverRef}
      id={id}
      data-testid='entity-hover-popover'
      data-component='entity-hover-popover'
      // Opt out of `useModalOverlay`'s `ariaHideOutside({ shouldUseInert: true })`.
      // When a slideout (react-aria-components Modal) is open, every element
      // outside its overlay subtree — including portals to document.body —
      // gets `inert` applied, which blocks pointer events. That manifested as
      // hovering a chip inside the slideout: the popover opens, but the
      // moment the cursor enters the popover surface, no `pointerenter` fires
      // (the element is inert), so the badge's HIDE_DELAY timer fires and the
      // popover dismisses. The same chip worked fine in non-modal contexts
      // (CRM tables) because no Modal was active to call ariaHideOutside.
      //
      // `data-react-aria-top-layer="true"` is the documented escape hatch
      // (see @react-aria/overlays/ariaHideOutside): the initial DOM walk
      // adds matching elements to `visibleNodes`, and the MutationObserver
      // watching for late-mounted portals does the same — so the popover
      // stays interactive regardless of when it mounts relative to the modal.
      data-react-aria-top-layer='true'
      className={cx(
        // Positioning + stacking — required for portal placement.
        'fixed',
        // Sizing — fixed-ish width keeps the surface compact and predictable
        // across all entity types. Matches Attio-style hover cards.
        'w-80 max-w-[calc(100vw-16px)]',
        // Card chrome — solid surface with subtle border and soft elevation.
        // Uses design system tokens so dark mode and theming are automatic.
        'rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)]',
        // `shadow-lg` is the ladder's transient-overlay level — the same one
        // menus, popovers and tooltips use.
        'shadow-lg',
        // Clip inner content to the rounded corners (e.g. CrmObjectCard's
        // inner content container would otherwise poke out of the radius).
        'overflow-hidden',
        className
      )}
      style={{ zIndex: stackingZIndex }}
      role='dialog'
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>,
    document.body
  );
}
